import { useCallback, useState } from 'react'
import { useTaskboard } from '../lib/taskboardContext.js'
import { dueMeta, bellMeta, tsToDate, filterTasks } from '../lib/format.js'
import { BoardRail, BoardColumn, useBoardDrag, Chip, Avatar, Button } from '../ds/index.js'
import BoardColumnMenu from '../components/BoardColumnMenu.jsx'
import CardMoveMenu from '../components/CardMoveMenu.jsx'
import { ChatCircle } from '@phosphor-icons/react'

export default function BoardView({ tasks, labelFilter, assigneeFilter, search, incompleteOnly, dueSoonOnly }) {
  const { usersById, labelsById, statuses, statusesById, openTask, moveTaskToStatus, quickAddTask, updateStatus, deleteStatus, createStatus } = useTaskboard()
  const [menuOpen, setMenuOpen] = useState(null)
  const [moveOpen, setMoveOpen] = useState(null)
  const [addingCol, setAddingCol] = useState(null)
  const [addDraft, setAddDraft] = useState('')
  const [addingStatus, setAddingStatus] = useState(false)
  const [statusDraft, setStatusDraft] = useState('')

  const onDrop = useCallback((taskId, statusId, index) => moveTaskToStatus(taskId, statusId, index), [moveTaskToStatus])

  const projTasks = filterTasks(tasks, { labelFilter, assigneeFilter, incompleteOnly, dueSoonOnly, search, statusesById })
  const orderSignature = projTasks.map((t) => `${t.id}:${t.status}:${t.order}`).join('|')
  const { containerRef, getCardHandlers, getColProps, draggingId, wasDragging } = useBoardDrag({ onDrop, orderSignature })

  const sortedStatuses = [...statuses].sort((a, b) => a.order - b.order)

  const submitAdd = (id) => {
    if (addDraft.trim()) quickAddTask(addDraft, id)
    setAddDraft('')
  }

  const submitAddStatus = () => {
    const trimmed = statusDraft.trim()
    if (trimmed) {
      const maxOrder = statuses.length ? Math.max(...statuses.map((s) => s.order)) : -1
      createStatus({ name: trimmed, order: maxOrder + 1, dot: '#7A7568', color: '#7A7568', done: false })
    }
    setStatusDraft('')
    setAddingStatus(false)
  }

  const moveStatus = (idx, dir) => {
    const reordered = [...sortedStatuses]
    const [moved] = reordered.splice(idx, 1)
    reordered.splice(idx + dir, 0, moved)
    reordered.forEach((s, i) => { if (s.order !== i) updateStatus(s.id, { order: i }) })
  }

  return (
    <BoardRail innerRef={containerRef}>
      {sortedStatuses.map((s, idx) => {
        const cards = projTasks.filter((t) => t.status === s.id).sort((a, b) => a.order - b.order)
        return (
          <BoardColumn
            key={s.id}
            title={s.name}
            count={cards.length}
            accent={s.dot}
            {...getColProps(s.id)}
            actions={
              <BoardColumnMenu
                open={menuOpen === s.id}
                onToggle={() => setMenuOpen((v) => (v === s.id ? null : s.id))}
                onClose={() => setMenuOpen(null)}
                name={s.name}
                renameValue={s.name}
                onRename={(name) => updateStatus(s.id, { name })}
                onRecolor={(c) => updateStatus(s.id, { color: c, dot: c })}
                recolorValue={s.color || s.dot}
                onMoveLeft={() => moveStatus(idx, -1)}
                onMoveRight={() => moveStatus(idx, 1)}
                canMoveLeft={idx > 0}
                canMoveRight={idx < sortedStatuses.length - 1}
                onDelete={() => deleteStatus(s.id)}
                confirmNote="Tasks keep this status id."
              />
            }
            footer={
              addingCol === s.id ? (
                <input
                  value={addDraft}
                  onChange={(e) => setAddDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitAdd(s.id)
                    else if (e.key === 'Escape') {
                      setAddingCol(null)
                      setAddDraft('')
                    }
                  }}
                  onBlur={() => {
                    setAddingCol(null)
                    setAddDraft('')
                  }}
                  autoFocus
                  placeholder="Task name — Enter to add"
                  className="cn-input"
                  style={{ fontSize: 13, padding: '9px 11px' }}
                />
              ) : (
                <button
                  onClick={() => {
                    setAddingCol(s.id)
                    setAddDraft('')
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 13, padding: '8px 4px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-gold)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-muted)')}
                >
                  <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> Add task
                </button>
              )
            }
          >
            {cards.map((t) => {
              const assignee = usersById[t.assigneeUid]
              const done = !!statusesById[t.status]?.done
              const meta = dueMeta(tsToDate(t.dueDate), done)
              const bell = bellMeta(meta, done)
              const dragging = draggingId === t.id
              return (
                <div
                  key={t.id}
                  {...getCardHandlers(t.id)}
                  data-dragging={dragging ? 'true' : 'false'}
                  onClick={() => { if (!wasDragging()) openTask(t.id) }}
                  style={{ border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-lg)', background: 'var(--surface-card)', padding: '12px 13px', cursor: 'grab', transition: 'border-color .12s' }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-gold)')}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-divider-dark)')}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 10 }}>
                    {bell.show && <span style={bell.style}>{bell.glyph}</span>}
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--color-white)', lineHeight: 1.4 }}>{t.title}</div>
                    <CardMoveMenu
                      open={moveOpen === t.id}
                      onToggle={() => setMoveOpen((v) => (v === t.id ? null : t.id))}
                      onClose={() => setMoveOpen(null)}
                      columns={sortedStatuses.map((s) => ({ id: s.id, name: s.name, dot: s.dot }))}
                      currentId={t.status}
                      onMove={(destId) => moveTaskToStatus(t.id, destId)}
                    />
                  </div>
                  {(t.labelIds || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
                      {t.labelIds.map((id) =>
                        labelsById[id] ? (
                          <Chip key={id} variant="solid" color={labelsById[id].color}>{labelsById[id].name}</Chip>
                        ) : null,
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar user={assignee} size={24} />
                    {meta.hasDue && (
                      <span style={{ color: meta.overdue ? 'var(--color-danger)' : 'var(--color-muted)', fontSize: 11, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>
                        {meta.label}
                      </span>
                    )}
                    <div style={{ flex: 1 }} />
                    {t.commentCount > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--color-muted)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                        <ChatCircle size={13} weight="fill" />
                        {t.commentCount}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </BoardColumn>
        )
      })}

      {/* Ghost "Add status" column */}
      <div
        style={{
          flex: '0 0 auto',
          minWidth: 220,
          border: '1px dashed var(--color-divider-dark)',
          borderRadius: 'var(--radius-lg)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '13px 14px',
          opacity: 0.55,
          transition: 'opacity .15s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.55')}
      >
        {addingStatus ? (
          <div style={{ width: '100%' }}>
            <input
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitAddStatus()
                if (e.key === 'Escape') { setAddingStatus(false); setStatusDraft('') }
              }}
              onBlur={() => submitAddStatus()}
              autoFocus
              placeholder="Status name — Enter to add"
              className="cn-input"
              style={{ fontSize: 13, padding: '9px 11px', width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        ) : (
          <Button variant="ghost" onClick={() => { setAddingStatus(true); setStatusDraft('') }} style={{ fontSize: 13, padding: '4px 0' }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Add status
          </Button>
        )}
      </div>
    </BoardRail>
  )
}
